import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import app, { db, storage } from "../firebase";
import { useCuerpo } from "../context/CuerpoContext";

const REGIONES = [
  "Arica y Parinacota", "Tarapacá", "Antofagasta", "Atacama", "Coquimbo",
  "Valparaíso", "Metropolitana de Santiago", "O'Higgins", "Maule", "Ñuble",
  "Biobío", "La Araucanía", "Los Ríos", "Los Lagos", "Aysén", "Magallanes"
];

const MENSAJE_INACTIVO_DEFAULT =
  "Este Cuerpo de Bomberos se encuentra temporalmente desactivado por Digital Evolution BM. Para mayor información, comuníquese con la administración de su institución.";

function slugify(texto = "") {
  return texto.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

function idDesdeSlug(slug) { return slug || `cuerpo-${Date.now()}`; }

function validarImagen(file) {
  if (!file) return true;
  if (!file.type?.startsWith("image/")) { alert("El archivo seleccionado debe ser una imagen."); return false; }
  if (file.size > 5 * 1024 * 1024) { alert("El logo no puede superar 5 MB."); return false; }
  return true;
}

const vacio = {
  nombre: "", comuna: "", region: "", slug: "", logoURL: "", activo: true,
  mensajeDesactivado: MENSAJE_INACTIVO_DEFAULT,
};

export default function GestionCuerpos() {
  const { cuerpoId, seleccionarCuerpoPorId } = useCuerpo();
  const [cuerpos, setCuerpos] = useState([]);
  const [form, setForm] = useState(vacio);
  const [logoArchivo, setLogoArchivo] = useState(null);
  const [editandoId, setEditandoId] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = async () => {
    setCargando(true);
    try {
      const snap = await getDocs(collection(db, "cuerpos_bomberos"));
      const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      arr.sort((a, b) => Number(a.orden || 9999) - Number(b.orden || 9999) || String(a.nombre || "").localeCompare(String(b.nombre || "")));
      setCuerpos(arr);
      return arr;
    } finally { setCargando(false); }
  };

  useEffect(() => { cargar().catch(console.error); }, []);

  const proximoOrden = useMemo(() => cuerpos.reduce((m, c) => Math.max(m, Number(c.orden || 0)), 0) + 1, [cuerpos]);

  const cambiar = (campo, valor) => setForm(prev => {
    const next = { ...prev, [campo]: valor };
    if (!editandoId && campo === "comuna") next.slug = slugify(valor);
    if (!editandoId && campo === "nombre" && !next.comuna) next.slug = slugify(valor.replace(/^cuerpo de bomberos de\s+/i, "").replace(/^cuerpo de bomberos\s+/i, ""));
    return next;
  });

  const cancelar = () => { setEditandoId(null); setForm(vacio); setLogoArchivo(null); };

  const editar = (c) => {
    setEditandoId(c.id);
    setForm({
      nombre: c.nombre || "", comuna: c.comuna || "", region: c.region || "", slug: c.slug || "",
      logoURL: c.logoURL || c.logo_url || c.logoUrl || c.logo || "", activo: c.activo !== false,
      mensajeDesactivado: c.mensajeDesactivado || c.mensaje_desactivado || MENSAJE_INACTIVO_DEFAULT,
    });
    setLogoArchivo(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const validarSlugUnico = async (slug) => {
    const snap = await getDocs(query(collection(db, "cuerpos_bomberos"), where("slug", "==", slug)));
    return snap.docs.every(d => d.id === editandoId);
  };

  const subirLogo = async (archivo, slug) => {
    if (!archivo) return form.logoURL.trim();
    const extension = (archivo.name.split(".").pop() || "png").replace(/[^a-zA-Z0-9]/g, "").toLowerCase() || "png";
    const archivoRef = ref(storage, `logos_cuerpos/${slug}.${extension}`);
    await uploadBytes(archivoRef, archivo, { contentType: archivo.type || undefined });
    return await getDownloadURL(archivoRef);
  };

  const guardar = async () => {
    const nombre = form.nombre.trim(), comuna = form.comuna.trim(), region = form.region.trim();
    const slug = slugify(form.slug || comuna || nombre);
    if (!nombre || !comuna || !region || !slug) return alert("Completa nombre, comuna, región y slug.");
    if (!validarImagen(logoArchivo)) return;

    setGuardando(true);
    try {
      if (!(await validarSlugUnico(slug))) return alert("Ese slug ya está siendo utilizado por otro Cuerpo.");
      const logoURL = await subirLogo(logoArchivo, slug);
      const datos = {
        nombre, comuna, region, slug, logoURL, activo: !!form.activo,
        mensajeDesactivado: String(form.mensajeDesactivado || "").trim() || MENSAJE_INACTIVO_DEFAULT,
        updated_at: Timestamp.now(),
      };
      if (editandoId) {
        await updateDoc(doc(db, "cuerpos_bomberos", editandoId), datos);
        alert("Cuerpo actualizado correctamente.");
      } else {
        const nuevoId = idDesdeSlug(slug);
        await setDoc(doc(db, "cuerpos_bomberos", nuevoId), { ...datos, orden: proximoOrden, created_at: Timestamp.now() });
        alert("Cuerpo creado correctamente. Ya puedes seleccionarlo y comenzar a configurarlo.");
      }
      cancelar(); await cargar();
    } catch (e) { console.error(e); alert("No se pudo guardar el Cuerpo: " + e.message); }
    finally { setGuardando(false); }
  };

  const alternarActivo = async (c) => {
    const nuevo = c.activo === false;
    const texto = nuevo ? "activar" : "desactivar";
    const aviso = nuevo
      ? `¿Seguro que deseas activar ${c.nombre}?`
      : `¿Seguro que deseas desactivar ${c.nombre}?\n\nSus datos NO serán eliminados. Las páginas públicas mostrarán el mensaje de desactivación de Digital Evolution BM.`;
    if (!window.confirm(aviso)) return;
    try {
      await updateDoc(doc(db, "cuerpos_bomberos", c.id), {
        activo: nuevo,
        mensajeDesactivado: c.mensajeDesactivado || c.mensaje_desactivado || MENSAJE_INACTIVO_DEFAULT,
        updated_at: Timestamp.now(),
      });
      await cargar();
    } catch (e) { console.error(e); alert("No se pudo cambiar el estado: " + e.message); }
  };

  const eliminarCuerpo = async (c) => {
    const texto = window.prompt(
      `ELIMINACIÓN DEFINITIVA\n\nSe borrarán bomberos, compañías, unidades, operadoras, citaciones, partes, auditoría, banners, administradores asociados, logos y demás datos vinculados a este Cuerpo.\n\nEsta acción no se puede deshacer.\n\nEscribe exactamente el nombre para confirmar:\n${c.nombre}`
    );
    if (texto !== c.nombre) {
      if (texto !== null) alert("El nombre no coincide. No se eliminó nada.");
      return;
    }
    if (!window.confirm(`Última confirmación: ¿eliminar definitivamente ${c.nombre} y TODOS sus datos relacionados?`)) return;

    setEliminandoId(c.id);
    try {
      const fn = httpsCallable(getFunctions(app), "eliminarCuerpoCompleto");
      const result = await fn({ cuerpoId: c.id, confirmacion: c.nombre });
      const resumen = result?.data || {};
      alert(`Cuerpo eliminado correctamente.\nDocumentos eliminados: ${resumen.documentosEliminados ?? "—"}\nUsuarios Auth eliminados: ${resumen.usuariosAuthEliminados ?? "—"}`);
      const restantes = await cargar();
      if (c.id === cuerpoId && restantes.length) await seleccionarCuerpoPorId(restantes[0].id);
    } catch (e) {
      console.error(e);
      alert("No se pudo eliminar completamente el Cuerpo: " + (e?.message || e));
    } finally { setEliminandoId(null); }
  };

  const logoPreview = logoArchivo ? URL.createObjectURL(logoArchivo) : form.logoURL;

  return (
    <div style={{ maxWidth: 1150 }}>
      <h2>Cuerpos de Bomberos</h2>
      <p>Solo el superadmin puede crear, desactivar y eliminar clientes de la plataforma.</p>

      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 18, marginBottom: 28 }}>
        <h3>{editandoId ? "Editar Cuerpo" : "Crear nuevo Cuerpo"}</h3>
        <div style={{ display: "grid", gap: 10, maxWidth: 760 }}>
          <input value={form.nombre} onChange={e => cambiar("nombre", e.target.value)} placeholder="Nombre (ej. Cuerpo de Bomberos de Río Bueno)" />
          <input value={form.comuna} onChange={e => cambiar("comuna", e.target.value)} placeholder="Comuna" />
          <select value={form.region} onChange={e => cambiar("region", e.target.value)}><option value="">-- Región --</option>{REGIONES.map(r => <option key={r}>{r}</option>)}</select>
          <input value={form.slug} onChange={e => cambiar("slug", slugify(e.target.value))} placeholder="slug público (ej. rio-bueno)" />

          <label style={{ fontWeight: 600 }}>Logo del Cuerpo
            <input type="file" accept="image/*" onChange={e => setLogoArchivo(e.target.files?.[0] || null)} style={{ display: "block", marginTop: 6 }} />
          </label>
          {logoPreview && <img src={logoPreview} alt="Vista previa" style={{ maxWidth: 150, maxHeight: 150, objectFit: "contain", border: "1px solid #ddd", borderRadius: 8, padding: 6 }} />}

          <label><input type="checkbox" checked={form.activo} onChange={e => cambiar("activo", e.target.checked)} /> Cuerpo activo</label>

          <label style={{ fontWeight: 600 }}>Mensaje cuando el Cuerpo esté desactivado</label>
          <textarea
            value={form.mensajeDesactivado}
            onChange={e => cambiar("mensajeDesactivado", e.target.value)}
            rows={4}
            placeholder={MENSAJE_INACTIVO_DEFAULT}
          />

          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={guardar} disabled={guardando}>{guardando ? "Guardando..." : (editandoId ? "Guardar cambios" : "Crear Cuerpo")}</button>
            {editandoId && <button onClick={cancelar}>Cancelar</button>}
          </div>
        </div>
      </div>

      <h3>Cuerpos registrados</h3>
      {cargando ? <p>Cargando...</p> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead><tr><th>Logo</th><th>Orden</th><th>Nombre</th><th>Comuna</th><th>Región</th><th>Slug</th><th>Estado</th><th>Acciones</th></tr></thead>
            <tbody>
              {cuerpos.map(c => {
                const logo = c.logoURL || c.logo_url || c.logoUrl || c.logo || "";
                return <tr key={c.id} style={{ background: c.id === cuerpoId ? "#f1f8ff" : "transparent" }}>
                  <td>{logo ? <img src={logo} alt="" style={{ width: 52, height: 52, objectFit: "contain" }} /> : "—"}</td>
                  <td>{c.orden ?? "—"}</td><td>{c.nombre || c.id}</td><td>{c.comuna || "—"}</td><td>{c.region || "—"}</td><td>{c.slug || "—"}</td><td>{c.activo === false ? "Inactivo" : "Activo"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button onClick={() => seleccionarCuerpoPorId(c.id)}>Administrar</button>{" "}
                    <button onClick={() => editar(c)}>Editar</button>{" "}
                    <button onClick={() => alternarActivo(c)}>{c.activo === false ? "Activar" : "Desactivar"}</button>{" "}
                    <button disabled={eliminandoId === c.id} onClick={() => eliminarCuerpo(c)} style={{ color: "#b42318", fontWeight: 700 }}>
                      {eliminandoId === c.id ? "Eliminando..." : "Eliminar definitivamente"}
                    </button>
                  </td>
                </tr>;
              })}
              {!cuerpos.length && <tr><td colSpan="8">No hay Cuerpos registrados.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
